import { Container } from '../ui/Container/Container';
import { useScrollReveal } from './useScrollReveal';

const PROBLEMS = [
  {
    num: '01',
    bg: 'rgba(91,138,181,0.08)',
    color: '#5b8ab5',
    title: 'One generation at a time',
    desc: "Can't run multiple versions in parallel. Each attempt is a fresh coin flip with no connection to the last.",
  },
  {
    num: '02',
    bg: 'rgba(150,120,180,0.08)',
    color: '#9678b4',
    title: 'Locked to one model',
    desc: 'One subscription, one family of models. Want FLUX and Nano Banana and Sora? Three subscriptions.',
  },
  {
    num: '03',
    bg: 'rgba(200,212,90,0.1)',
    color: '#a8b44a',
    title: 'No reference control',
    desc: 'Try telling a chat "use image #3 with prompt #7." It doesn\'t work. Context gets lost in the scroll.',
  },
  {
    num: '04',
    bg: 'rgba(62,69,87,0.06)',
    color: '#3e4557',
    title: 'Editing is a gamble',
    desc: "Giving feedback and editing parts of the image is hard. Most tools don't follow instructions for targeted edits.",
  },
];

export function LandingProblem() {
  const ref = useScrollReveal<HTMLDivElement>();

  return (
    <section className="py-12 md:py-20 bg-[#f5f7fa]">
      <Container size="lg" className="max-w-[1100px]">
        <div ref={ref} className="lp-reveal text-center">
          <p className="font-mono text-xs uppercase tracking-[0.14em] text-primary-500 mb-3">
            The problem
          </p>
          <h2
            className="text-[clamp(1.75rem,4vw,2.6rem)] font-semibold leading-[1.15] tracking-tight mb-3"
            style={{ color: '#1d2939' }}
          >
            Chat-style image gen
            <br />
            is a{' '}
            <em className="not-italic" style={{ fontFamily: "'Playfair Display',Georgia,serif", fontStyle: 'italic', fontWeight: 400, color: '#6b529e' }}>
              slot machine.
            </em>
          </h2>
          <p className="text-[clamp(0.95rem,2vw,1.1rem)] text-[#5a6478] max-w-[540px] mx-auto leading-relaxed">
            You type. You pray. You regenerate. And every tool locks you into one model, one chat, one roll of the dice.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 mt-8">
          {PROBLEMS.map((p) => (
            <div
              key={p.num}
              className="bg-white border border-black/[0.06] rounded-[14px] p-5 transition-all hover:border-black/[0.1] hover:shadow-[0_4px_16px_rgba(0,0,0,0.04)]"
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center mb-3 text-[0.85rem] font-bold"
                style={{ background: p.bg, color: p.color }}
              >
                {p.num}
              </div>
              <h3 className="text-[0.88rem] font-semibold text-[#1d2939] mb-1">{p.title}</h3>
              <p className="text-[0.78rem] text-[#5a6478] leading-[1.5]">{p.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <p className="text-[1.05rem] text-[#5a6478]">
            What if you could use{' '}
            <em style={{ fontFamily: "'Playfair Display',Georgia,serif", fontStyle: 'italic', color: '#6b529e', fontSize: '1.15rem' }}>
              every model, visually, without the complexity?
            </em>
          </p>
        </div>
      </Container>
    </section>
  );
}
